import { memo, useCallback, useMemo, useState } from 'react'
import { z } from 'zod'
import { type Node, type NodeProps, Position } from '@xyflow/react'
import { useShallow } from 'zustand/shallow'
import { useFlowStore } from '@/stores/flow-store'
import { useDeleteNode } from '@/hooks/use-delete-node'
import CustomHandle from '@/features/flow/_builder/components/handles/custom-handler'
import {
  NodeCard,
  NodeCardContent,
  NodeCardDescription,
  NodeCardFooter,
  NodeCardHeader,
} from '@/features/flow/_builder/components/ui/node-card'
import IntegrationPropertyPanel from '../../sidebar/panels/node-properties/property-panels/integration-property-panel'
import { BuilderNode, RegisterNodeMetadata } from '../../types'
import { getNodeDetail } from '../../utils'

const NODE_TYPE = BuilderNode.INTEGRATION

const IntegrationNodeDataSchema = z.object({
  method: z.enum(['GET', 'POST', 'PUT', 'DELETE', 'PATCH'], {
    message: 'Método HTTP deve ser GET, POST, PUT, PATCH ou DELETE',
  }),
  url: z
    .string()
    .min(1, 'URL da integração é obrigatória')
    .refine(
      (url) => {
        try {
          new URL(url)
          return true
        } catch {
          return false
        }
      },
      {
        message: 'URL deve ter um formato válido (ex: https://api.exemplo.com)',
      }
    ),
  headers: z.record(z.string()),
  body: z.string(),
  timeoutMs: z
    .number()
    .min(1000, 'Timeout deve ser pelo menos 1000ms (1s)')
    .max(300000, 'Timeout deve ser no máximo 300000ms (5min)'),
  deletable: z.boolean().optional(),
})

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
export type IntegrationNodeData = z.infer<typeof IntegrationNodeDataSchema>

type IntegrationNodeProps = NodeProps<
  Node<IntegrationNodeData, typeof NODE_TYPE>
>

export function IntegrationNode({
  id,
  isConnectable,
  selected,
  data,
}: IntegrationNodeProps) {
  const meta = useMemo(() => getNodeDetail(NODE_TYPE), [])

  const [showNodePropertiesOf] = useFlowStore(
    useShallow((s) => [s.actions.sidebar.showNodePropertiesOf])
  )

  const [sourceHandleId] = useState<string>(`${id}-source`)
  const [targetHandleId] = useState<string>(`${id}-target`)

  const deleteNode = useDeleteNode()

  const handleDeleteNode = () => {
    deleteNode(id)
  }

  const handleShowNodeProperties = useCallback(() => {
    showNodePropertiesOf({ id, type: NODE_TYPE })
  }, [id, showNodePropertiesOf])

  return (
    <NodeCard
      data-selected={selected}
      onDoubleClick={handleShowNodeProperties}
      nodeId={id}
    >
      <NodeCardHeader
        icon={meta.icon}
        title={meta.title}
        handleDeleteNode={handleDeleteNode}
        handleShowNodeProperties={handleShowNodeProperties}
        gradientColor={meta.gradientColor}
      />

      <NodeCardContent>
        <div className='flex flex-col p-4'>
          <div className='text-xs font-medium text-card-foreground'>
            Requisição
          </div>
          <div className='mt-2 text-sm leading-snug'>
            <span className='rounded bg-card-foreground/10 px-1 py-0.5'>
              {data.method}
            </span>
            <span className='ml-2 break-all text-card-foreground/80'>
              {data.url || '—'}
            </span>
          </div>
        </div>

        <NodeCardDescription description='Conecta com sistemas externos via Webhook/APIs' />

        <NodeCardFooter nodeId={id} />
      </NodeCardContent>

      <CustomHandle
        type='target'
        id={targetHandleId}
        position={Position.Left}
        isConnectable={isConnectable}
      />

      <CustomHandle
        type='source'
        id={sourceHandleId}
        position={Position.Right}
        isConnectable={isConnectable}
      />
    </NodeCard>
  )
}

export const metadata: RegisterNodeMetadata<IntegrationNodeData> = {
  type: NODE_TYPE,
  node: memo(IntegrationNode),
  detail: {
    icon: 'plug-zap',
    title: 'Integração',
    description: 'Integrar com CRMs, agendamentos, etc via HTTP',
    gradientColor: 'green',
  },
  connection: {
    inputs: 1,
    outputs: 1,
  },
  defaultData: {
    method: 'POST',
    url: '',
    headers: {},
    body: '',
    timeoutMs: 15000,
  },
  propertyPanel: IntegrationPropertyPanel,
  validationSchema: IntegrationNodeDataSchema,
}
